import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, Chip, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, MenuItem
} from '@mui/material';
import { ArrowBack, SwapHoriz } from '@mui/icons-material';
import axios from 'axios';
import { useLocations } from '../hooks/useLocations';
import { fmt, sym } from '../utils/currency';

const TYPE_COLOR = {
  Purchase:             'success',
  Requisition:          'warning',
  Transfer:             'info',
  'Positive Adjustment':'success',
  'Negative Adjustment':'error'
};

const ItemLedgerEntries = () => {
  const { itemNo }  = useParams();
  const navigate    = useNavigate();
  const { locations } = useLocations();

  const [entries,   setEntries]   = useState([]);
  const [item,      setItem]      = useState(null);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [loading,   setLoading]   = useState(true);
  const [relocateOpen,       setRelocateOpen]       = useState(false);
  const [relocateLocation,   setRelocateLocation]   = useState('');
  const [relocating,         setRelocating]         = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2000);
    return () => clearTimeout(t);
  }, [success]);

  const load = async () => {
    try {
      const [ledgerRes, itemsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/item-ledger?itemNo=${itemNo}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/items`, { headers })
      ]);
      setEntries(ledgerRes.data);
      setItem(itemsRes.data.find(i => i.itemNo === itemNo) || { itemNo, itemName: itemNo });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load item ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [itemNo]); // eslint-disable-line

  const handleRelocate = async () => {
    if (!relocateLocation) { setError('Select a target location'); return; }
    setRelocating(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payables/item-ledger/relocate`,
        { itemNo, toLocationCode: relocateLocation },
        { headers }
      );
      setRelocateOpen(false);
      setRelocateLocation('');
      setSuccess(data.message);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to relocate stock');
    } finally {
      setRelocating(false);
    }
  };

  const closingQty   = entries.length > 0 ? entries[entries.length - 1].runningQty   : 0;
  const closingValue = entries.length > 0 ? entries[entries.length - 1].runningValue : 0;

  // Unlocated = entries with blank locationCode still contributing positive qty
  const unlocatedQty = entries
    .filter(e => e.locationCode === '' || e.locationCode === null || e.locationCode === undefined)
    .reduce((s, e) => s + e.quantity, 0);

  if (loading) return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading item ledger...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Button startIcon={<ArrowBack />} onClick={() => navigate('/payables/items')} sx={{ mr: 2 }}>
                Back
              </Button>
              <Box>
                <Typography variant="h5" color="primary">Item Ledger</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item?.itemName} — {itemNo}
                </Typography>
              </Box>
            </Box>
            {unlocatedQty > 0 && (
              <Button startIcon={<SwapHoriz />} variant="outlined" color="warning" size="small"
                onClick={() => setRelocateOpen(true)}>
                Relocate {unlocatedQty.toFixed(2)} unlocated units
              </Button>
            )}
          </Box>

          {/* Stock summary */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <Chip
              label={`Stock on Hand: ${closingQty.toFixed(2)} ${item?.unitOfMeasure || ''}`}
              color={closingQty > 0 ? 'success' : closingQty < 0 ? 'error' : 'default'}
              variant="outlined"
              sx={{ fontWeight: 'bold', fontSize: '0.95rem', px: 1 }}
            />
            <Chip
              label={`Inventory Value: ${sym()} ${fmt(closingValue)}`}
              color="primary" variant="outlined" sx={{ fontWeight: 600 }}
            />
            {closingQty > 0 && (
              <Chip
                label={`Avg Cost: ${sym()} ${fmt(closingValue / closingQty)} / ${item?.unitOfMeasure || 'Ea'}`}
                variant="outlined"
              />
            )}
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {entries.length === 0 ? (
            <Typography color="text.secondary">No inventory transactions yet for this item.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Document No</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell align="right"><strong>Qty In</strong></TableCell>
                  <TableCell align="right"><strong>Qty Out</strong></TableCell>
                  <TableCell align="right"><strong>Running Qty</strong></TableCell>
                  <TableCell align="right"><strong>Unit Cost</strong></TableCell>
                  <TableCell align="right"><strong>Value ({sym()})</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow key={e._id} hover
                    sx={{ fontWeight: i === entries.length - 1 ? 700 : 'normal' }}>
                    <TableCell>{new Date(e.postingDate).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{e.documentNo}</TableCell>
                    <TableCell>
                      <Chip label={e.entryType} color={TYPE_COLOR[e.entryType] || 'default'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell>
                      {e.locationCode
                        ? <Chip label={e.locationCode} size="small" variant="outlined" />
                        : <Typography variant="caption" color="warning.main" fontStyle="italic">unlocated</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {e.quantity > 0 ? e.quantity.toFixed(2) : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {e.quantity < 0 ? Math.abs(e.quantity).toFixed(2) : ''}
                    </TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: i === entries.length - 1 ? 700 : 'normal',
                            color: e.runningQty < 0 ? 'error.main' : 'inherit' }}>
                      {e.runningQty.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">{(e.unitCost || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: e.amount < 0 ? 'error.main' : 'inherit' }}>
                      {Math.abs(e.amount).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Relocate dialog */}
      <Dialog open={relocateOpen} onClose={() => setRelocateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Relocate Unlocated Stock</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {unlocatedQty.toFixed(2)} units of {item?.itemName} have no location assigned (posted before
            location tracking was set up). Select where to assign them.
          </DialogContentText>
          <TextField select label="Move to Location" value={relocateLocation}
            onChange={e => setRelocateLocation(e.target.value)} fullWidth size="small">
            <MenuItem value=""><em>Select Location</em></MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc.locationCode} value={loc.locationCode}>
                {loc.locationCode} – {loc.locationName}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRelocateOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRelocate} disabled={relocating}>
            {relocating ? 'Relocating…' : 'Relocate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemLedgerEntries;
